import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Menu, MapPin, Activity, DollarSign, FileText, Shield, QrCode, X, BadgeDollarSign } from 'lucide-react';
import NavButton from './NavButton';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import qr from '../assets/dashboard_qr.png';
import formQr from '../assets/form.png';
import driLogoLight from '../assets/DRi_logo_v3_white (2).png';
import driLogoDark from '../assets/DRi_Logo_dark.png';

export const navItems = [
  { to: '/equipment-location', icon: MapPin, label: 'Equipment Location' },
  { to: '/equipment-status', icon: Activity, label: 'Equipment Status' },
  { to: '/finance', icon: DollarSign, label: 'Finance' },
  { to: '/drive', icon: BadgeDollarSign, label: 'DRiVE' },
  { to: '/log-details', icon: FileText, label: 'Log Details' },
  { to: '/admin-dashboard', icon: Shield, label: 'Admin', adminOnly: true },
];

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed';

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  });
  const [activeQr, setActiveQr] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [compactScreen, setCompactScreen] = useState(false);
  const { theme } = useTheme();
  const { user } = useAuth();
  const isLight = theme === 'light';
  const topLogo = isLight ? driLogoDark : driLogoLight;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (collapsed) {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(SIDEBAR_STORAGE_KEY);
    }
  }, [collapsed]);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1366px)');
    const updateCompact = (event) => {
      setCompactScreen(event.matches);
    };

    updateCompact(query);
    query.addEventListener('change', updateCompact);

    return () => query.removeEventListener('change', updateCompact);
  }, []);

  useEffect(() => {
    window.isViewingQr = Boolean(activeQr);

    if (!activeQr) {
      return () => {
        window.isViewingQr = false;
      };
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveQr(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.isViewingQr = false;
    };
  }, [activeQr]);

  const expandedWidth = compactScreen ? 216 : 248;
  const collapsedWidth = compactScreen ? 80 : 88;

  const containerClasses = `${
    isLight
      ? 'border-slate-200/60 bg-white/85 text-slate-900 shadow-xl shadow-slate-200/70'
      : 'border-white/10 bg-slate-950/80 text-slate-100 shadow-2xl shadow-slate-900/60'
  } hidden md:flex h-screen max-h-screen flex-col overflow-hidden border-r backdrop-blur-2xl transition-colors md:sticky md:top-0 md:self-start`;

  const navPanelClasses = collapsed
    ? `flex flex-col items-center gap-1.5 rounded-3xl border border-white/10 bg-white/5 p-2 shadow-[0_14px_32px_rgba(15,23,42,0.16)] backdrop-blur-xl dark:border-white/5 dark:bg-white/5`
    : `flex flex-col gap-1.5 rounded-3xl border border-white/10 bg-white/5 p-2.5 shadow-[0_18px_40px_rgba(15,23,42,0.2)] backdrop-blur-xl dark:border-white/5 dark:bg-white/5`;

  const sectionBorderClass = isLight ? 'border-slate-200/60' : 'border-white/10 dark:border-white/5';

  const qrCards = [
    {
      id: 'form',
      label: 'QR Form',
      image: formQr,
    },
    {
      id: 'dashboard',
      label: 'Dash QR',
      image: qr,
    },
  ];

  return (
    <>
      <motion.aside
        animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className={containerClasses}
        aria-label="Primary sidebar"
      >
        <div
          className={`relative flex h-24 items-center justify-center border-b px-4 ${sectionBorderClass}`}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.18),transparent_55%),radial-gradient(circle_at_bottom,rgba(16,185,129,0.18),transparent_60%)]" />
          {!collapsed && (
            <img src={topLogo} alt="Digi Rock Innovations logo" className="relative h-10 w-auto" />
          )}
          <button
            className={`absolute right-4 flex h-10 w-10 items-center justify-center rounded-2xl border text-slate-700 transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-400/60 dark:text-slate-100 ${
              isLight
                ? 'border-slate-200/80 bg-white/70 hover:border-slate-200 hover:bg-white'
                : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
            }`}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            aria-controls="primary-nav"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>

        <nav
          id="primary-nav"
          className={`${collapsed ? 'px-2.5 py-5' : 'px-4 py-5'} flex-1 overflow-y-auto`}
        >
          <div className={navPanelClasses}>
            {navItems
              .filter((item) => !(item.adminOnly && (!user || user.visitor)))
              .map((item) => (
                <NavButton key={item.to} {...item} collapsed={collapsed} />
              ))}
          </div>
        </nav>

        {!collapsed && (
          <div className="space-y-4 px-5 pb-8 pt-2">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3 shadow-[0_12px_28px_rgba(15,23,42,0.16)] backdrop-blur-xl dark:border-white/5 dark:bg-white/5">
              <div className="flex items-center gap-2">
                {qrCards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => setActiveQr(card)}
                    className="group flex flex-1 flex-col items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-center text-xs font-semibold text-slate-800 transition hover:-translate-y-0.5 hover:border-emerald-300/60 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-emerald-300/60 dark:border-white/10 dark:bg-white/5 dark:text-white"
                    aria-label={`View ${card.label}`}
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/20 text-emerald-500 shadow-inner shadow-black/10 transition group-hover:scale-105 group-hover:text-emerald-400 dark:border-white/10 dark:bg-white/10">
                      <QrCode className="h-4 w-4" />
                    </span>
                    <span className="truncate">{card.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <a
              href="https://digirockinnovations.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-300/70 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:-translate-y-0.5 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-300/60 dark:border-emerald-400/60 dark:text-emerald-100 dark:hover:bg-white/5"
            >
              Contact Us
            </a>
        </div>
      )}
      </motion.aside>

      {mounted &&
        activeQr &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/85 p-6 backdrop-blur"
          >
            <div className="relative w-full max-w-xl overflow-hidden rounded-4xl border border-white/10 bg-slate-950/90 p-8 text-center shadow-[0_50px_140px_rgba(15,23,42,0.7)] md:p-10">
              <button
                type="button"
                onClick={() => setActiveQr(null)}
                className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/10 text-slate-200 transition hover:border-white/20 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-emerald-300/60"
                aria-label="Close enlarged QR"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="space-y-4">
                <img
                  src={activeQr.image}
                  alt={`${activeQr.label} code`}
                  className="mx-auto w-full max-w-[18rem] rounded-3xl border border-white/10 bg-white p-5 shadow-inner shadow-black/20 sm:p-6 md:max-w-[20rem]"
                />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-white sm:text-xl">{activeQr.label}</h3>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
